import React, { useState, useEffect, useCallback, memo, useMemo } from "react";
import { Modal, ToggleSwitch, RichTextEditor, HtmlRenderer } from "./index";
import { MdClose } from "react-icons/md";

// Reusable style constants for consistency
const STYLE_CONSTANTS = {
  labelClass: "block text-sm font-semibold text-gray-800 mb-2",
  inputClass: "w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-[#FB2C37] focus:ring-1 focus:ring-[#FB2C37] bg-white",
  containerClass: "border-2 border-gray-200 rounded-lg p-4 bg-white hover:border-gray-300 transition-colors",
  toggleLabelClass: "text-base font-semibold text-gray-900 cursor-pointer block truncate hover:text-black transition-colors",
  sectionTitleClass: "font-semibold text-black mb-3 text-base uppercase tracking-wide bg-gray-100 px-3 py-2 rounded-md"
} as const;

// Utility function for label generation
const getLabel = (type: "link" | "content"): string =>
  type === "link" ? "Link:" : "Content:";

export interface SectionToggleItem {
  label: string;
  key: string;
  checked: boolean;
  description?: string;
  urlKey?: string;
  urlValue?: string;
  urlPlaceholder?: string;
  contentKey?: string;
  contentValue?: string;
  contentPlaceholder?: string;
}

export interface SectionGroup {
  title: string;
  items: SectionToggleItem[];
}

export interface SectionSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  sections: ReadonlyArray<SectionGroup>;
  onSave: (completeData: Record<string, any>) => void;
  title?: string;
}

/**
 * SectionSettingsModal - Reusable modal for managing section visibility toggles
 * Can be used by any widget that needs toggle controls for different sections
 */
export const SectionSettingsModal: React.FC<SectionSettingsModalProps> = ({
  isOpen,
  onClose,
  sections,
  onSave,
  title = ""
}) => {
  console.log('SectionSettingsModal',sections)
  const [editingContentKey, setEditingContentKey] = useState<string | null>(null);
  
  // LOCAL STATE for form data - prevents re-renders during typing
  const [localSections, setLocalSections] = useState<SectionGroup[]>([]);

  // Initialize local state when modal opens
  useEffect(() => {
    if (isOpen && sections.length > 0) {
      setLocalSections(sections.map(section => ({
        ...section,
        items: section.items.map(item => ({ ...item }))
      })));
    }
  }, [isOpen, sections]);

  const handleSave = useCallback(() => {
    // Build complete update object with ALL changes from local state
    const completeUpdateData: Record<string, any> = {};
    
    localSections.forEach(section => {
      section.items.forEach(item => {
        // Update toggle states
        completeUpdateData[item.key] = item.checked;
        
        // Update URL fields
        if (item.urlKey) {
          completeUpdateData[item.urlKey] = item.urlValue || "";
        }
        
        // Update content fields
        if (item.contentKey) {
          completeUpdateData[item.contentKey] = item.contentValue || "";
        }
      });
    });
    // PARENT CALLBACK - Send all data to parent component at once
    onSave(completeUpdateData);
    onClose();
  }, [localSections, onSave, onClose]);

  const handleCancel = useCallback(() => {
    setEditingContentKey(null);
    onClose();
  }, [onClose]);

  // LOCAL toggle change - no parent updates until Save
  const handleLocalToggleChange = useCallback((key: string, checked: boolean) => {
    setLocalSections(prev => {
      const newSections = prev.map(section => ({
        ...section,
        items: section.items.map(item => 
          item.key === key ? { ...item, checked } : item
        )
      }));
      return newSections;
    });
  }, []);

  // LOCAL field change - no parent updates until Save  
  const handleLocalFieldChange = useCallback((key: string, value: string) => {
    setLocalSections(prev => {
      const newSections = prev.map(section => ({
        ...section,
        items: section.items.map(item => {
          if (item.urlKey === key) {
            return { ...item, urlValue: value };
          }
          if (item.contentKey === key) {
            return { ...item, contentValue: value };
          }
          return item;
        })
      }));
      
      return newSections;
    });
  }, []);

  const handleContentEditClick = useCallback((contentKey: string) => {
    setEditingContentKey(contentKey);
  }, []);

  const handleContentEditBlur = useCallback(() => {
    setEditingContentKey(null);
  }, []);


  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <div className="flex flex-col h-full max-h-[80vh]">
        {/* Fixed Header Section */}
        <div className="flex-shrink-0 pb-4">
          {/* Description */}
          <p className="text-sm text-gray-600">
            Control which sections are visible to visitors on your website. 
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto pr-2 space-y-6">
          {/* Sections - Better Spacing */}
          <div className="space-y-6">
          {localSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="border-b border-gray-300 pb-4 last:border-b-0">
              {/* Section Title - Enhanced Visibility */}
              <h4 className={STYLE_CONSTANTS.sectionTitleClass}>
                {section.title}
              </h4>
              
              {/* Section Items - Enhanced spacing */}
              <div className="space-y-3">
                {section.items.map((item) => (
                  <MemoizedSectionItem
                    key={item.key}
                    item={item}
                    sectionTitle={section.title}
                    editingContentKey={editingContentKey}
                    onToggleChange={handleLocalToggleChange}
                    onFieldChange={handleLocalFieldChange}
                    onContentEditClick={handleContentEditClick}
                    onContentEditBlur={handleContentEditBlur}
                  />
                ))}
              </div>
            </div>
          ))}
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 flex justify-end gap-3 mt-4 pt-3 border-t border-gray-200">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
            aria-label="Cancel changes and close modal"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded hover:bg-red-600 transition-colors"
            aria-label="Save changes and close modal"
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Memoized SectionItem component for better performance
interface SectionItemProps {
  item: SectionToggleItem;
  sectionTitle: string;
  editingContentKey: string | null;
  onToggleChange: (key: string, checked: boolean) => void;
  onFieldChange?: (key: string, value: string) => void;
  onContentEditClick: (contentKey: string) => void;
  onContentEditBlur: () => void;
}

const SectionItem = memo<SectionItemProps>(({
  item,
  sectionTitle,
  editingContentKey,
  onToggleChange,
  onFieldChange,
  onContentEditClick,
  onContentEditBlur
}) => {
  // Memoized labels to avoid recalculation on each render
  const linkLabel = useMemo(() => getLabel("link"), []);
  const contentLabel = useMemo(() => getLabel("content"), []);
  
  // Memoized content editor state for aria-expanded
  const isContentEditorOpen = useMemo(() => 
    editingContentKey === item.contentKey, [editingContentKey, item.contentKey]
  );
  return (
    <div className={STYLE_CONSTANTS.containerClass}>
      {/* Toggle Header - Enhanced Visibility */}
      <div className="flex items-center">
        <div className="flex-1 min-w-0">
          <label 
            htmlFor={`toggle-${item.key}`}
            className={STYLE_CONSTANTS.toggleLabelClass}
          >
            {item.label}
          </label>
          {item.description && (
            <p className="text-sm text-gray-700 mt-2 leading-relaxed" id={`desc-${item.key}`}>
              {item.description}
            </p>
          )}
        </div>
        
        <div className="ml-4 flex-shrink-0">
          <ToggleSwitch
            isVisible={item.checked}
            onToggle={(visible: boolean) => onToggleChange(item.key, visible)}
            size="md"
            showLabel={false}
            showIcon={true}
            aria-labelledby={`toggle-${item.key}`}
            aria-describedby={item.description ? `desc-${item.key}` : undefined}
          />
        </div>
      </div>

      {/* Conditional Input Fields */}
      {item.checked && (item.urlKey || item.contentKey) && (
        <div className="space-y-4 pt-3 mt-3 border-t-2 border-gray-200">
          {/* URL Input Field - Enhanced Visibility */}
          {item.urlKey && onFieldChange && (
            <div className="w-full">
              <label 
                htmlFor={`url-${item.urlKey}`}
                className={STYLE_CONSTANTS.labelClass}
              >
                {linkLabel}
              </label>
                <input
                  id={`url-${item.urlKey}`}
                  type="text"
                  value={item.urlValue || ""}
                  onChange={(e) => onFieldChange?.(item.urlKey!, e.target.value)}
                  placeholder={item.urlPlaceholder || "Enter URL or use content editor"}
                  className={STYLE_CONSTANTS.inputClass}
                  autoComplete="off"
                  aria-describedby={`url-help-${item.urlKey}`}
                />
              <p id={`url-help-${item.urlKey}`} className="sr-only">
                Enter a valid URL for {item.label}
              </p>
            </div>
          )}

          {/* Centered OR divider - Only show when both URL and Content fields are present */}
          {item.urlKey && item.contentKey && onFieldChange && (
            <div className="flex items-center justify-center py-3">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="px-4 text-sm font-medium text-gray-500 bg-white">OR</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>
          )}

          {/* Rich Text Content Editor - Enhanced UX */}
          {item.contentKey && onFieldChange && (
            <div className="w-full">
              <label 
                htmlFor={`content-${item.contentKey}`}
                className={STYLE_CONSTANTS.labelClass}
              >
                {contentLabel}
              </label>
              
              {editingContentKey === item.contentKey ? (
                <div className="w-full border border-green-500 rounded overflow-hidden relative">
                  <button
                    onClick={onContentEditBlur}
                    className="absolute top-2 right-2 z-10 p-1 text-gray-500 hover:text-gray-700 hover:bg-white rounded transition-colors"
                    aria-label="Close editor"
                  >
                    <MdClose size={16} />
                  </button>
                  <RichTextEditor
                    value={item.contentValue || ""}
                    onChange={(content) => onFieldChange?.(item.contentKey!, content)}
                    onBlur={onContentEditBlur}
                    placeholder={item.contentPlaceholder || "Enter content..."}
                    minHeight={100}
                    className="text-sm w-full"
                    aria-label={`Content editor for ${item.label}`}
                  />
                </div>
              ) : (
                  <div
                  className="w-full border-2 border-dashed border-gray-400 rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 min-h-[120px] flex flex-col cursor-pointer"
                  onClick={() => onContentEditClick(item.contentKey!)}
                  role="button"
                  aria-label={`Click to edit content for ${item.label}`}
                  aria-expanded={isContentEditorOpen}
                  aria-live="polite"
                >
                  {item.contentValue && item.contentValue.trim() ? (
                    <HtmlRenderer 
                      html={item.contentValue || ""}
                      className="text-sm text-gray-800 flex-1"
                      preserveWhiteText={true}
                    />
                  ) : (
                    <div className="text-sm text-gray-600 italic flex-1 flex items-center font-medium" aria-live="polite">
                      {item.contentPlaceholder || "Click to add content..."}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

SectionItem.displayName = 'SectionItem';

// Custom comparison function to prevent unnecessary re-renders that cause focus loss
const arePropsEqual = (prevProps: SectionItemProps, nextProps: SectionItemProps) => {
  // Only re-render if the specific item's data actually changed (keys don't change, so no need to compare)
  return (
    prevProps.item.urlValue === nextProps.item.urlValue &&
    prevProps.item.contentValue === nextProps.item.contentValue &&
    prevProps.item.checked === nextProps.item.checked &&
    prevProps.editingContentKey === nextProps.editingContentKey &&
    prevProps.sectionTitle === nextProps.sectionTitle
  );
};

// Apply custom comparison to prevent focus loss during typing
const MemoizedSectionItem = memo(SectionItem, arePropsEqual);

export default SectionSettingsModal;
